import React from 'react';

// ============================================================================
// COMPONENT 1: Area-by-Year Biomass Table (EXISTING)
// ============================================================================
export const ScallopBiomassTable = ({ samsData, baselineSamsData }) => {
  // Helper function to format numbers with commas and 1 decimal place
  const formatNumber = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '—';
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: 1, 
      maximumFractionDigits: 1 
    });
  };

  // Helper function to calculate percentage change
  const calculatePercentChange = (current, baseline) => {
    if (!baseline || baseline === 0) return null;
    return ((current - baseline) / baseline) * 100;
  };

  // Helper function to format the cell value with percentage change
  const formatCellWithChange = (currentValue, baselineValue) => {
    const formattedValue = formatNumber(currentValue);
    
    if (!baselineValue || baselineValue === 0) {
      return (
        <span>
          {formattedValue} <span style={{ color: '#6b7280' }}>(--)</span>
        </span>
      );
    }

    const percentChange = calculatePercentChange(currentValue, baselineValue);
    const sign = percentChange >= 0 ? '+' : '';
    const color = percentChange >= 0 ? '#10b981' : '#ef4444'; // green or red

    return (
      <span>
        {formattedValue}{' '}
        <span style={{ color, fontWeight: '600' }}>
          ({sign}{percentChange.toFixed(1)}%)
        </span>
      </span>
    );
  };

  // Extract unique years from the data (should be projection years >= 2025)
  const allYears = [...new Set(samsData.map(row => row.year))].sort((a, b) => a - b);
  
  // Take only the next 3-4 years from the projections
  const years = allYears.slice(0, 4);

  // Extract unique areas
  const areas = [...new Set(samsData.map(row => row.sams_area))].filter(Boolean).sort();

  // If no data, show a message
  if (!samsData || samsData.length === 0 || years.length === 0 || areas.length === 0) {
    return (
      <div className="scallop-tables-container">
        <p className="no-data-message">No SAMS projection data available</p>
      </div>
    );
  }

  // Create a lookup map for quick data access: area -> year -> exploitable_biomass
  const dataMap = {};
  samsData.forEach(row => {
    if (!dataMap[row.sams_area]) {
      dataMap[row.sams_area] = {};
    }
    dataMap[row.sams_area][row.year] = row.exploitable_biomass_mt || 0;
  });

  // Create baseline lookup map if baseline data exists
  const baselineMap = {};
  if (baselineSamsData && baselineSamsData.length > 0) {
    baselineSamsData.forEach(row => {
      if (!baselineMap[row.sams_area]) {
        baselineMap[row.sams_area] = {};
      }
      baselineMap[row.sams_area][row.year] = row.exploitable_biomass_mt || 0;
    });
  }

  // Calculate totals for each year
  const yearTotals = {};
  const baselineYearTotals = {};
  
  years.forEach(year => {
    yearTotals[year] = areas.reduce((sum, area) => {
      return sum + (dataMap[area]?.[year] || 0);
    }, 0);

    if (baselineSamsData && baselineSamsData.length > 0) {
      baselineYearTotals[year] = areas.reduce((sum, area) => {
        return sum + (baselineMap[area]?.[year] || 0);
      }, 0);
    }
  });

  return (
    <div className="scallop-tables-container">
      <div className="table-title">SAMS Area Projections: Exploitable Biomass (mt)</div>
      <div className="table-wrapper">
        <table className="scallop-table">
          <thead>
            <tr>
              <th className="area-header">Area</th>
              {years.map(year => (
                <th key={year} className="year-header">{year}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {areas.map((area, index) => (
              <tr key={area} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                <td className="area-cell">{area}</td>
                {years.map(year => {
                  const currentValue = dataMap[area]?.[year] || 0;
                  const baselineValue = baselineMap[area]?.[year];
                  
                  return (
                    <td key={`${area}-${year}`} className="data-cell">
                      {formatCellWithChange(currentValue, baselineValue)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td className="total-label">TOTAL</td>
              {years.map(year => {
                const currentTotal = yearTotals[year];
                const baselineTotal = baselineYearTotals[year];
                
                return (
                  <td key={`total-${year}`} className="total-cell">
                    {formatCellWithChange(currentTotal, baselineTotal)}
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENT 2: Metric Summary Table
// ============================================================================
export const ScallopMetricSummary = ({ summaryData, baselineSummaryData, loading, error }) => {
  // Helper to format numbers
  const formatNumber = (num, decimals = 1) => {
    if (num === null || num === undefined || isNaN(num)) return '—';
    return num.toLocaleString('en-US', { 
      minimumFractionDigits: decimals, 
      maximumFractionDigits: decimals 
    });
  };

  // Helper function to calculate percentage change
  const calculatePercentChange = (current, baseline) => {
    if (!baseline || baseline === 0) return null;
    return ((current - baseline) / baseline) * 100;
  };

  // Helper function to format the cell value with percentage change
  const formatCellWithChange = (currentValue, baselineValue, decimals = 1) => {
    const formattedValue = formatNumber(currentValue, decimals);
    
    if (!baselineValue || baselineValue === 0) {
      return (
        <span>
          {formattedValue} <span style={{ color: '#6b7280' }}>(--)</span>
        </span>
      );
    }

    const percentChange = calculatePercentChange(currentValue, baselineValue);
    const sign = percentChange >= 0 ? '+' : '';
    const color = percentChange >= 0 ? '#10b981' : '#ef4444'; // green or red

    return (
      <span>
        {formattedValue}{' '}
        <span style={{ color, fontWeight: '600' }}>
          ({sign}{percentChange.toFixed(1)}%)
        </span>
      </span>
    );
  };

  if (loading) {
    return (
      <div className="scallop-tables-container">
        <p className="loading-message">Loading metric summary...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="scallop-tables-container">
        <p className="error-message">{error}</p>
      </div>
    );
  }

  if (!summaryData || summaryData.length === 0) {
    return (
      <div className="scallop-tables-container">
        <p className="no-data-message">No summary data available</p>
      </div>
    );
  }

  // Create baseline lookup map if baseline data exists
  const baselineMap = {};
  if (baselineSummaryData && baselineSummaryData.length > 0) {
    baselineSummaryData.forEach(row => {
      baselineMap[row.area] = row;
    });
  }

  // Calculate fleet-wide totals
  const totals = {
    mean_F: summaryData.reduce((sum, row) => sum + (row.mean_F || 0), 0) / summaryData.length,
    mean_DAS: summaryData.reduce((sum, row) => sum + (row.mean_DAS || 0), 0),
    mean_LPUE: summaryData.reduce((sum, row) => sum + (row.mean_LPUE || 0), 0) / summaryData.length,
    total_catch: summaryData.reduce((sum, row) => sum + (row.total_catch_mt || 0), 0),
    mean_biomass: summaryData.reduce((sum, row) => sum + (row.mean_exploitable_biomass_mt || 0), 0) / summaryData.length
  };

  // Calculate baseline totals if baseline exists
  const baselineTotals = baselineSummaryData && baselineSummaryData.length > 0 ? {
    mean_F: baselineSummaryData.reduce((sum, row) => sum + (row.mean_F || 0), 0) / baselineSummaryData.length,
    mean_DAS: baselineSummaryData.reduce((sum, row) => sum + (row.mean_DAS || 0), 0),
    mean_LPUE: baselineSummaryData.reduce((sum, row) => sum + (row.mean_LPUE || 0), 0) / baselineSummaryData.length,
    total_catch: baselineSummaryData.reduce((sum, row) => sum + (row.total_catch_mt || 0), 0),
    mean_biomass: baselineSummaryData.reduce((sum, row) => sum + (row.mean_exploitable_biomass_mt || 0), 0) / baselineSummaryData.length
  } : null;

  return (
    <div className="scallop-tables-container">
      <div className="table-title">SAMS Projection Summary: Area Performance Metrics</div>
      <div className="table-subtitle">Averaged across entire projection period</div>
      <div className="table-wrapper">
        <table className="scallop-table">
          <thead>
            <tr>
              <th className="area-header" style={{ width: '15%' }}>Area</th>
              <th className="metric-header" style={{ width: '12%' }}>
                Mean F<br/>
                <span style={{ fontSize: '11px', fontWeight: 'normal', opacity: 0.7 }}>(yr⁻¹)</span>
              </th>
              {/* <th className="metric-header" style={{ width: '12%' }}>
                Mean DAS<br/>
                <span style={{ fontSize: '11px', fontWeight: 'normal', opacity: 0.7 }}>(days)</span>
              </th> */}
              <th className="metric-header" style={{ width: '14%' }}>
                Mean LPUE<br/>
                <span style={{ fontSize: '11px', fontWeight: 'normal', opacity: 0.7 }}>(lbs/day)</span>
              </th>
              <th className="metric-header" style={{ width: '14%' }}>
                Total Catch<br/>
                <span style={{ fontSize: '11px', fontWeight: 'normal', opacity: 0.7 }}>(mt)</span>
              </th>
              <th className="metric-header" style={{ width: '16%' }}>
                Mean Biomass<br/>
                <span style={{ fontSize: '11px', fontWeight: 'normal', opacity: 0.7 }}>(mt)</span>
              </th>
              <th className="metric-header" style={{ width: '10%' }}>
                Years<br/>
                <span style={{ fontSize: '11px', fontWeight: 'normal', opacity: 0.7 }}>(n)</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {summaryData.map((row, index) => {
              const baselineRow = baselineMap[row.area];
              
              return (
                <tr key={row.area} className={index % 2 === 0 ? 'even-row' : 'odd-row'}>
                  <td className="area-cell">{row.area}</td>
                  <td className="data-cell">
                    {formatCellWithChange(row.mean_F, baselineRow?.mean_F, 4)}
                  </td>
                  <td className="data-cell">
                    {formatCellWithChange(row.mean_DAS, baselineRow?.mean_DAS, 2)}
                  </td>
                  <td className="data-cell">
                    {formatCellWithChange(row.mean_LPUE, baselineRow?.mean_LPUE, 1)}
                  </td>
                  <td className="data-cell">
                    {formatCellWithChange(row.total_catch_mt, baselineRow?.total_catch_mt, 1)}
                  </td>
                  <td className="data-cell">
                    {formatCellWithChange(row.mean_exploitable_biomass_mt, baselineRow?.mean_exploitable_biomass_mt, 1)}
                  </td>
                  <td className="data-cell">{row.years_in_projection}</td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="total-row">
              <td className="total-label">FLEET AVG/TOTAL</td>
              <td className="total-cell">
                {formatCellWithChange(totals.mean_F, baselineTotals?.mean_F, 4)}
              </td>
              <td className="total-cell">
                {formatCellWithChange(totals.mean_DAS, baselineTotals?.mean_DAS, 2)}
              </td>
              <td className="total-cell">
                {formatCellWithChange(totals.mean_LPUE, baselineTotals?.mean_LPUE, 1)}
              </td>
              <td className="total-cell">
                {formatCellWithChange(totals.total_catch, baselineTotals?.total_catch, 1)}
              </td>
              <td className="total-cell">
                {formatCellWithChange(totals.mean_biomass, baselineTotals?.mean_biomass, 1)}
              </td>
              <td className="total-cell">—</td>
            </tr>
          </tfoot>
        </table>
      </div>
      
      {/* Legend */}
      <div style={{ 
        marginTop: '12px', 
        padding: '10px', 
        background: 'rgba(255,255,255,0.05)', 
        borderRadius: '6px',
        fontSize: '12px',
        color: 'rgba(255,255,255,0.7)'
      }}>
        <strong>Metrics Explained:</strong>
        <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
          <li><strong>Mean F:</strong> Average fishing mortality rate (annual)</li>
          <li><strong>Mean DAS:</strong> Average days-at-sea allocated per timestep</li>
          <li><strong>Mean LPUE:</strong> Average landings per unit effort - indicates catch efficiency</li>
          <li><strong>Total Catch:</strong> Sum of all catch over the projection period</li>
          <li><strong>Mean Biomass:</strong> Average exploitable biomass (100mm scallops)</li>
        </ul>
      </div>
    </div>
  );
};

// ============================================================================
// DEFAULT EXPORT: Component that wraps both tables
// ============================================================================
const ScallopTables = ({ samsData, baselineSamsData }) => {
  return <ScallopBiomassTable samsData={samsData} baselineSamsData={baselineSamsData} />;
};

export default ScallopTables;